import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useDebounce } from '@/shared/lib';
import {
  useAssetsIcons,
  useCountriesDictionary,
  useCreateDepositOperation,
  useDepositCommission,
  useDepositWithdrawAssetsDictionary,
} from '@/shared/api';
import { COMMISSION_CHECK_DELAY } from '@/shared/config';
import type {
  Country,
  DepositFormValues,
  DepositSelectOption,
} from '@/shared/types';
import { ACCOUNT_TYPES, useAccountsInfo } from '@/entities/account';
import {
  useBankDepositBanks,
  useBankDepositCountries,
  useBankDepositCurrencies,
} from '../api';

export interface BankTransferFormValues extends DepositFormValues {
  bankCountryCode: string;
  bankKey: string;
}

interface UseDepositBankTransferFormLogicOptions {
  onSubmit?: (data: BankTransferFormValues) => void;
  onSuccess?: (invoiceId: string) => void;
  onError?: (error: Error) => void;
  initialData?: BankTransferFormValues | null;
}

type FiatLimit = { code: string; min: number; max?: number };

export const useDepositBankTransferFormLogic = ({
  onSubmit,
  onSuccess,
  onError,
  initialData,
}: UseDepositBankTransferFormLogicOptions = {}) => {
  const { t } = useTranslation();
  const [, setFormData] = useState<BankTransferFormValues | null>(
    initialData || null
  );

  const { accountsInfo } = useAccountsInfo();
  const accountNumber =
    accountsInfo?.accounts.find(acc => acc.accTypeName === ACCOUNT_TYPES.Main)
      ?.accNum || '';

  const { depositWithdrawAssetsData } = useDepositWithdrawAssetsDictionary();
  const { assetsIconsData } = useAssetsIcons();

  const {
    countriesData,
    isCountriesLoading: isCountriesDictionaryLoading,
    countriesError: countriesDictionaryError,
  } = useCountriesDictionary();

  const [currentMinAmount, setCurrentMinAmount] = useState(0);
  const [currentMaxAmount, setCurrentMaxAmount] = useState<number>();
  const [currentCurrencyCode, setCurrentCurrencyCode] = useState('');

  const bankTransferForm = useForm<BankTransferFormValues>({
    mode: 'onChange',
    resolver: zodResolver(
      z.object({
        bankCountryCode: z
          .string()
          .nonempty({ message: t('authPages.validation.requiredField') }),
        currencyCode: z
          .string()
          .nonempty({ message: t('authPages.validation.requiredField') }),
        bankKey: z
          .string()
          .nonempty({ message: t('authPages.validation.requiredField') }),
        amount: z
          .string({ error: t('authPages.validation.requiredField') })
          .refine(
            val => (currentMinAmount ? Number(val) >= currentMinAmount : true),
            {
              message: currentMinAmount
                ? t('depositMethods.bankTransfer.validation.minAmount', {
                    amount: currentMinAmount,
                    currency: currentCurrencyCode || '',
                  })
                : t('authPages.validation.requiredField'),
            }
          )
          .refine(
            val => (currentMaxAmount ? Number(val) <= currentMaxAmount : true),
            {
              message: t('common.maxAmount', {
                amount: currentMaxAmount,
                currency: currentCurrencyCode || '',
              }),
            }
          ),
      })
    ),
    defaultValues: {
      bankCountryCode: initialData?.bankCountryCode || '',
      currencyCode: initialData?.currencyCode || '',
      bankKey: initialData?.bankKey || '',
      amount: String(initialData?.amount),
    },
  });

  const watchedValues = bankTransferForm.watch();

  const { countries, isCountriesLoading, countriesError } =
    useBankDepositCountries();

  const { currencies, isCurrenciesFetching, currenciesError } =
    useBankDepositCurrencies(watchedValues.bankCountryCode);

  const { banks, isBanksFetching, banksError } = useBankDepositBanks(
    watchedValues.bankCountryCode,
    watchedValues.currencyCode
  );

  const filteredCountries = useMemo<Country[]>(() => {
    return countries.map(country => {
      const dictionaryCountry = countriesData?.value?.find(
        item => item.code === country.countryCode
      );

      return {
        code: country.countryCode,
        name: country.countryName,
        link: dictionaryCountry?.link ?? '',
        phoneCode: dictionaryCountry?.phoneCode ?? '',
        isRestricted: dictionaryCountry?.isRestricted ?? false,
      };
    });
  }, [countries, countriesData]);

  const bankFiatLimits = useMemo(() => {
    const fiat = depositWithdrawAssetsData?.value?.deposit?.fiat ?? [];
    const bankGroup = fiat.find(item => Object.keys(item)[0] === 'bank') as
      | Record<string, FiatLimit[]>
      | undefined;

    return new Map((bankGroup?.bank ?? []).map(item => [item.code, item]));
  }, [depositWithdrawAssetsData]);

  const currencyOptions = useMemo<DepositSelectOption[]>(() => {
    return currencies.map(currency => ({
      value: currency.code,
      label: currency.code,
      name: currency.name,
      icon: assetsIconsData?.value?.[currency.code] ?? '',
      min: bankFiatLimits.get(currency.code)?.min ?? 0,
      max: bankFiatLimits.get(currency.code)?.max,
    }));
  }, [currencies, assetsIconsData, bankFiatLimits]);

  const bankOptions = useMemo(() => {
    return banks.map(bank => ({
      value: bank.bankKey,
      label: bank.bankName,
    }));
  }, [banks]);

  const calculatedMinAmount = useMemo(() => {
    if (
      !watchedValues.bankCountryCode ||
      !watchedValues.currencyCode ||
      !watchedValues.bankKey
    ) {
      return 0;
    }

    const selectedOption = currencyOptions.find(
      o => o.value === watchedValues.currencyCode
    );

    return selectedOption?.min || 0;
  }, [
    watchedValues.bankCountryCode,
    watchedValues.currencyCode,
    watchedValues.bankKey,
    currencyOptions,
  ]);

  const calculatedMaxAmount = useMemo(
    () =>
      currencyOptions.find(o => o.value === watchedValues.currencyCode)?.max,
    [watchedValues.currencyCode, currencyOptions]
  );

  useEffect(() => {
    setCurrentMinAmount(calculatedMinAmount);
    setCurrentMaxAmount(calculatedMaxAmount);
    setCurrentCurrencyCode(watchedValues.currencyCode);
  }, [calculatedMinAmount, calculatedMaxAmount, watchedValues.currencyCode]);

  const {
    mutate: createDeposit,
    isCreateOperationDepositLoading,
    createOperationDepositError,
  } = useCreateDepositOperation({
    operationType: 'bank',
    onSuccess: (data: { invoiceId: string }) => {
      onSuccess?.(data.invoiceId);
    },
    onError: error => {
      onError?.(error);
    },
  });

  const debouncedAmount = useDebounce(
    watchedValues.amount,
    COMMISSION_CHECK_DELAY
  );

  const normalizedAmount = watchedValues.amount ?? NaN;

  const hasCommissionInputs = Boolean(
    watchedValues.bankCountryCode &&
      watchedValues.currencyCode &&
      watchedValues.bankKey &&
      !Number.isNaN(normalizedAmount) &&
      Number(normalizedAmount) > 0
  );

  const isCommissionEnabled = !!(
    watchedValues.bankCountryCode &&
    watchedValues.currencyCode &&
    watchedValues.bankKey &&
    !Number.isNaN(debouncedAmount) &&
    Number(debouncedAmount) > 0
  );

  const {
    data: commissionData,
    isLoading: isCommissionFetching,
    error: commissionError,
  } = useDepositCommission({
    operationType: 'bank',
    params: {
      bankCountryCode: watchedValues.bankCountryCode,
      currencyCode: watchedValues.currencyCode,
      amount: debouncedAmount,
      bankKey: watchedValues.bankKey,
    },
    enabled: isCommissionEnabled,
  });

  useEffect(() => {
    bankTransferForm.setValue('currencyCode', '');
    bankTransferForm.setValue('bankKey', '');
  }, [watchedValues.bankCountryCode, bankTransferForm]);

  useEffect(() => {
    bankTransferForm.setValue('bankKey', '');
  }, [watchedValues.currencyCode, bankTransferForm]);

  const isAmountDebouncing =
    hasCommissionInputs && watchedValues.amount !== debouncedAmount;
  const isCommissionPending =
    hasCommissionInputs && (isAmountDebouncing || isCommissionFetching);
  const isCommissionData = commissionData && typeof commissionData === 'object';
  const shouldShowCommission =
    !Object.keys(bankTransferForm.formState.errors).length &&
    hasCommissionInputs &&
    !isCommissionPending &&
    isCommissionData &&
    !commissionError;

  const isSubmitInProgressWithoutErrors =
    (isCreateOperationDepositLoading || isCommissionPending) &&
    !(
      commissionError ||
      Object.keys(bankTransferForm.formState.errors).length > 0
    );

  const handleFormSubmit = (data: BankTransferFormValues) => {
    if (
      Object.keys(bankTransferForm.formState.errors).length > 0 ||
      Boolean(commissionError) ||
      isSubmitInProgressWithoutErrors ||
      !isCommissionData
    ) {
      return;
    }

    setFormData(data);
    createDeposit({ ...data, accountNumber });
    onSubmit?.(data);
  };

  const minAmount = currentMinAmount;
  const amountPlaceholder = currentCurrencyCode
    ? t('common.amountMinAmountPlaceholder', {
        min: minAmount,
        currency: currentCurrencyCode,
      })
    : t('common.amountPlaceholder');

  const transformedCommissionData =
    isCommissionData &&
    commissionData &&
    typeof commissionData === 'object' &&
    'feeAmount' in commissionData
      ? {
          feeAmount: (commissionData as { feeAmount: number }).feeAmount,
          currencyCode: watchedValues.currencyCode,
          receiveAmount:
            Number(normalizedAmount) -
            (commissionData as { feeAmount: number }).feeAmount,
        }
      : undefined;

  const isDependentListFetching = isCurrenciesFetching || isBanksFetching;
  const listsError = countriesError || currenciesError || banksError;

  return {
    form: bankTransferForm,
    watchedValues,
    handleFormSubmit,

    currencyOptions,
    minAmount,
    amountPlaceholder,
    filteredCountries,
    bankOptions,

    commissionData: transformedCommissionData,
    isCommissionFetching,
    commissionError,
    shouldShowCommission,
    isCommissionPending,

    isSubmitInProgressWithoutErrors,
    formError: commissionError || createOperationDepositError,

    isLoading: isCountriesLoading || isCountriesDictionaryLoading,
    isCurrenciesFetching,
    isBanksFetching,
    isDependentListFetching,
    listsError,
    isAccountInfoError: false,
    accountsInfoError: null,

    hasDataError: false,
    firstError: countriesDictionaryError,
  };
};
